/**
 * Settings-Karte fuer Notification-Ruhezeiten und Kanal-Overrides.
 *
 * Alle Optionen werden gesammelt und beim „Speichern"-Klick per PUT an
 * /api/notifications/settings geschickt. Der Backend-Endpoint liefert
 * gleichzeitig die im Code hinterlegten Slug-Defaults + Labels, sodass
 * das Frontend keine eigene Slug-Liste hardcoden muss.
 *
 * Punkt D aus dem Todos-Notifications-Plan (2026-07-01).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Bell, BellRing, Loader2, Lock, Save, RotateCcw, Send } from 'lucide-react';
import { api } from '@onvice/portal-shell-api';
import {
  canUsePush,
  isSubscribed as isPushSubscribed,
  subscribePush,
  unsubscribePush,
} from '../lib/pushNotifications';

type Channel = 'in_app' | 'push_web' | 'push_mobile' | 'email';

const CHANNEL_LABELS: Record<Channel, string> = {
  in_app: 'In-App',
  push_web: 'Web-Push',
  push_mobile: 'Mobile-Push',
  email: 'E-Mail',
};

// Nur die Channels, die der User heute tatsaechlich pflegen soll.
// push_mobile + email sind Platzhalter fuer Phase 5b/spaeter und werden
// versteckt bis die Backend-Zustellung dafuer da ist.
const VISIBLE_CHANNELS: Channel[] = ['in_app', 'push_web'];

interface SettingsResponse {
  success: boolean;
  settings: {
    quiet_hours_start: string | null;
    quiet_hours_end: string | null;
    quiet_weekends: boolean;
    quiet_holidays: boolean;
    channel_overrides: Record<string, Channel[]>;
  };
  slug_defaults: Record<string, Channel[]>;
  slug_labels: Record<string, string>;
  mandatory_slugs: string[];
  is_gf_or_admin: boolean;
}

export default function NotificationSettingsCard() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [quietStart, setQuietStart] = useState('');
  const [quietEnd, setQuietEnd] = useState('');
  const [weekends, setWeekends] = useState(false);
  const [holidays, setHolidays] = useState(false);
  const [overrides, setOverrides] = useState<Record<string, Channel[]>>({});
  const [defaults, setDefaults] = useState<Record<string, Channel[]>>({});
  const [labels, setLabels] = useState<Record<string, string>>({});
  const [mandatorySlugs, setMandatorySlugs] = useState<string[]>([]);
  const [isGfOrAdmin, setIsGfOrAdmin] = useState<boolean>(false);
  const [pushState, setPushState] = useState<'unknown' | 'unsupported' | 'off' | 'on' | 'denied'>('unknown');
  const [pushBusy, setPushBusy] = useState(false);
  const [pushMessage, setPushMessage] = useState('');

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setMessage('');
    try {
      const r = await api.get<SettingsResponse>('/notifications/settings');
      setQuietStart(r.settings.quiet_hours_start ?? '');
      setQuietEnd(r.settings.quiet_hours_end ?? '');
      setWeekends(r.settings.quiet_weekends);
      setHolidays(r.settings.quiet_holidays);
      setOverrides(r.settings.channel_overrides ?? {});
      setDefaults(r.slug_defaults ?? {});
      setLabels(r.slug_labels ?? {});
      setMandatorySlugs(r.mandatory_slugs ?? []);
      setIsGfOrAdmin(r.is_gf_or_admin ?? false);
    } catch (err) {
      setMessage((err as Error).message || 'Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const refreshPushState = useCallback(async (): Promise<void> => {
    if (!canUsePush) { setPushState('unsupported'); return; }
    if (typeof Notification !== 'undefined' && Notification.permission === 'denied') {
      setPushState('denied'); return;
    }
    const sub = await isPushSubscribed();
    setPushState(sub ? 'on' : 'off');
  }, []);

  useEffect(() => { void refreshPushState(); }, [refreshPushState]);

  const togglePush = async (): Promise<void> => {
    setPushBusy(true); setPushMessage('');
    try {
      if (pushState === 'on') {
        await unsubscribePush();
        // Prompt-Dismiss-Flag zuruecksetzen, damit der Login-Banner
        // beim naechsten Login wieder erscheint.
        try { localStorage.removeItem('push-prompt-dismissed'); } catch { /* ignore */ }
        setPushMessage('Web-Push deaktiviert.');
      } else {
        const r = await subscribePush();
        if (!r.ok) {
          setPushMessage(r.reason ?? 'Aktivierung fehlgeschlagen');
        } else {
          try { localStorage.removeItem('push-prompt-dismissed'); } catch { /* ignore */ }
          setPushMessage('Web-Push aktiviert.');
        }
      }
      await refreshPushState();
    } finally {
      setPushBusy(false);
    }
  };

  const slugList = useMemo(() => Object.keys(defaults).sort(), [defaults]);

  const effectiveChannels = (slug: string): Channel[] => {
    const ov = overrides[slug];
    if (ov) return ov;
    return defaults[slug] ?? ['in_app'];
  };

  const isMandatoryLocked = (slug: string, channel: Channel): boolean => {
    if (channel !== 'push_web') return false;
    if (isGfOrAdmin) return false;
    return mandatorySlugs.includes(slug);
  };

  const toggleChannel = (slug: string, channel: Channel): void => {
    // Pflicht-Slugs: push_web darf von normalen Anwendern nicht abgeschaltet werden.
    if (isMandatoryLocked(slug, channel)) return;
    const current = effectiveChannels(slug);
    const has = current.includes(channel);
    const next = has ? current.filter((c) => c !== channel) : [...current, channel];
    setOverrides((prev) => ({ ...prev, [slug]: next }));
  };

  const resetSlug = (slug: string): void => {
    setOverrides((prev) => {
      const next = { ...prev };
      delete next[slug];
      return next;
    });
  };

  const save = async (): Promise<void> => {
    setSaving(true);
    setMessage('');
    try {
      await api.put('/notifications/settings', {
        quiet_hours_start: quietStart || null,
        quiet_hours_end: quietEnd || null,
        quiet_weekends: weekends,
        quiet_holidays: holidays,
        channel_overrides: overrides,
      });
      setMessage('Gespeichert.');
    } catch (err) {
      setMessage((err as Error).message || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-test-anchor="benachrichtigungen"
      className="bg-card border border-default rounded-xl p-4 sm:p-5 mb-4"
    >
      <div className="flex items-center gap-2 mb-4">
        <Bell size={18} className="text-primary" />
        <h2 className="text-lg font-semibold">Benachrichtigungen</h2>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-muted-foreground py-3">
          <Loader2 size={14} className="animate-spin" /> Lade Einstellungen…
        </div>
      ) : (
        <>
          <section
            data-test-anchor="web-push-toggle"
            className="mb-5"
          >
            <h3 className="text-sm font-semibold mb-2">Web-Push</h3>
            <p className="text-xs text-muted-foreground mb-3">
              Bekommt dieses Geraet Betriebssystem-Benachrichtigungen (z.B. bei
              Compliance-Warnungen, Todos oder neuen Chats). Muss pro Geraet
              einmal aktiviert werden.
            </p>
            {pushState === 'unsupported' && (
              <div className="text-xs text-muted-foreground">Dieser Browser unterstuetzt Web-Push nicht.</div>
            )}
            {pushState === 'denied' && (
              <div className="text-xs text-rose-500">
                Benachrichtigungen sind fuer diese Seite in den Browser-Einstellungen blockiert.
                Bitte dort erst wieder erlauben, dann hier aktivieren.
              </div>
            )}
            {(pushState === 'off' || pushState === 'on' || pushState === 'unknown') && (
              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => void togglePush()}
                  disabled={pushBusy || pushState === 'unknown'}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-md border ${
                    pushState === 'on'
                      ? 'bg-emerald-600 text-white border-emerald-500 hover:bg-emerald-500'
                      : 'bg-background border-default hover:border-primary'
                  } disabled:opacity-50`}
                >
                  {pushBusy
                    ? <Loader2 size={14} className="animate-spin" />
                    : <BellRing size={14} />}
                  {pushState === 'on' ? 'Aktiv — deaktivieren' : 'Web-Push aktivieren'}
                </button>
                {pushState === 'on' && (
                  <button
                    type="button"
                    onClick={async () => {
                      setPushBusy(true); setPushMessage('');
                      try {
                        await api.post('/notifications/push-test');
                        setPushMessage('Test-Push gesendet — sollte gleich ankommen.');
                      } catch (e) {
                        setPushMessage(`Test fehlgeschlagen: ${(e as Error).message}`);
                      } finally { setPushBusy(false); }
                    }}
                    disabled={pushBusy}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-default bg-background hover:border-primary disabled:opacity-50"
                  >
                    <Send size={14} /> Test-Push an mich
                  </button>
                )}
                {pushMessage && <span className="text-xs text-muted-foreground">{pushMessage}</span>}
              </div>
            )}
          </section>

          <section className="mb-5">
            <h3 className="text-sm font-semibold mb-2">Ruhezeit</h3>
            <p className="text-xs text-muted-foreground mb-3">
              In diesem Zeitfenster werden Push-Nachrichten mit Prioritaet „normal" oder „niedrig"
              gesammelt und erst danach zugestellt. Dringende Nachrichten kommen sofort.
            </p>
            <div className="grid grid-cols-2 gap-3 max-w-md">
              <div>
                <label className="block text-xs text-muted-foreground mb-1">Beginn</label>
                <input
                  type="time"
                  value={quietStart}
                  onChange={(e) => setQuietStart(e.target.value)}
                  className="w-full h-10 px-3 rounded-md border border-default bg-background"
                />
              </div>
              <div>
                <label className="block text-xs text-muted-foreground mb-1">Ende</label>
                <input
                  type="time"
                  value={quietEnd}
                  onChange={(e) => setQuietEnd(e.target.value)}
                  className="w-full h-10 px-3 rounded-md border border-default bg-background"
                />
              </div>
            </div>
            <div className="mt-3 space-y-2">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={weekends}
                  onChange={(e) => setWeekends(e.target.checked)}
                  className="w-4 h-4"
                />
                Ganzes Wochenende (Sa+So) als Ruhezeit
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={holidays}
                  onChange={(e) => setHolidays(e.target.checked)}
                  className="w-4 h-4"
                />
                Feiertage als Ruhezeit
              </label>
            </div>
          </section>

          <section className="mb-5">
            <h3 className="text-sm font-semibold mb-2">Kanaele pro Ereignis</h3>
            <p className="text-xs text-muted-foreground mb-3">
              Standardkanaele sind vorbelegt. Toggles setzen einen persoenlichen Override.
              Zuruecksetzen faellt auf den System-Default zurueck.
            </p>
            {slugList.length === 0 ? (
              <div className="text-sm text-muted-foreground">Keine Ereignis-Typen bekannt.</div>
            ) : (
              <ul className="space-y-2">
                {slugList.map((slug) => {
                  const label = labels[slug] ?? slug;
                  const current = effectiveChannels(slug);
                  const isOverridden = slug in overrides;
                  return (
                    <li key={slug} className="border border-default rounded-md p-3">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div>
                          <div className="text-sm font-medium">{label}</div>
                          <div className="text-[11px] text-muted-foreground">{slug}</div>
                        </div>
                        {isOverridden && (
                          <button
                            type="button"
                            onClick={() => resetSlug(slug)}
                            className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
                            title="Auf Standard zuruecksetzen"
                          >
                            <RotateCcw size={11} /> Standard
                          </button>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {VISIBLE_CHANNELS.map((ch) => {
                          const active = current.includes(ch);
                          const locked = isMandatoryLocked(slug, ch);
                          return (
                            <button
                              key={ch}
                              type="button"
                              onClick={() => toggleChannel(slug, ch)}
                              disabled={locked}
                              title={locked ? 'Pflicht durch Admin — kann nicht deaktiviert werden' : undefined}
                              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs border ${
                                active
                                  ? 'bg-primary text-primary-foreground border-primary'
                                  : 'bg-background border-default text-muted-foreground hover:text-foreground'
                              } ${locked ? 'opacity-70 cursor-not-allowed' : ''}`}
                            >
                              {locked && <Lock size={10} />}
                              {CHANNEL_LABELS[ch]}
                            </button>
                          );
                        })}
                      </div>
                      {mandatorySlugs.includes(slug) && !isGfOrAdmin && (
                        <div className="mt-1 text-[10px] text-muted-foreground italic">
                          Pflicht-Benachrichtigung — Push kann nicht abgeschaltet werden.
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => void save()}
              disabled={saving}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              Speichern
            </button>
            {message && <span className="text-sm text-muted-foreground">{message}</span>}
          </div>
        </>
      )}
    </div>
  );
}
